/**
 * Perimeter Source — shared constants used by the perimeter upload modal,
 * the fire-file MultiFileUploader (when the perimeter subcategory is selected),
 * and the backend perimeter upload service. Stored as a key in
 * `current_perimeters.source_name` and in the CMS `field_perimeter_source`
 * list_string field.
 *
 * Both the frontend modals and the backend (via .js-extension nodenext import)
 * use this single source of truth — no value drift between layers.
 */

export type PerimeterSourceKey =
  | 'gps_ground'
  | 'gps_air'
  | 'satellite_hotspots'
  | 'satellite_imagery'
  | 'other'
  | 'unknown';

export interface PerimeterSourceOption {
  value: PerimeterSourceKey;
  label: string;
}

export const PERIMETER_SOURCE_OPTIONS: PerimeterSourceOption[] = [
  { value: 'gps_ground',         label: 'GPS Ground' },
  { value: 'gps_air',            label: 'GPS Air' },
  { value: 'satellite_hotspots', label: 'Satellite Hotspots' },
  { value: 'satellite_imagery',  label: 'Satellite Imagery' },
  { value: 'other',              label: 'Other' },
  { value: 'unknown',            label: 'Unknown' },
];

const VALUE_SET: ReadonlySet<string> = new Set(PERIMETER_SOURCE_OPTIONS.map((o) => o.value));
const LABEL_BY_VALUE: ReadonlyMap<string, string> = new Map(PERIMETER_SOURCE_OPTIONS.map((o) => [o.value, o.label]));

export function isPerimeterSourceKey(value: string | null | undefined): value is PerimeterSourceKey {
  return typeof value === 'string' && VALUE_SET.has(value);
}

/**
 * Render a stored perimeter source value as a human-readable label. Known
 * keys (e.g. `gps_air`) map to their display label (`GPS Air`); legacy
 * free-text values fall through unchanged. Null/empty returns null.
 */
export function formatPerimeterSource(value: string | null | undefined): string | null {
  if (!value) return null;
  return LABEL_BY_VALUE.get(value) ?? value;
}

// Keys for which the UI should nudge the user to elaborate in the Description.
export const PERIMETER_SOURCE_NEEDS_DESCRIPTION: ReadonlySet<PerimeterSourceKey> = new Set(['other', 'unknown']);

// CMS subcategory term name (in fire_file_subcategory) that activates the
// perimeter pipeline on the generic fire-file upload route. Name-match is
// fragile to CMS renames, but no config to keep in sync.
export const PERIMETER_SUBCATEGORY_NAME = 'Fire Perimeter Shapefile (ZIP)';
